// 텍스트 포맷팅 유틸리티 함수들

// 포맷팅된 텍스트를 저장하기 위한 데이터 구조
export const createFormattedText = (text, formatting = []) => ({
  text,
  formatting: formatting || []
});

// 포맷팅 정보 타입 정의
export const FORMAT_TYPES = {
  BOLD: 'bold',
  SIZE: 'size',
  TITLE: 'title' // 소제목 (크기 + 볼드 조합)
};

// 포맷팅 적용 함수
export const applyFormatting = (text, start, end, formatType, formatValue = null) => {
  const formatting = {
    start,
    end,
    type: formatType,
    value: formatValue
  };

  return formatting;
};

  // 포맷팅된 텍스트를 HTML로 렌더링하는 함수
export const renderFormattedText = (formattedText) => {
  if (!formattedText || typeof formattedText === 'string') {
    // HTML 엔티티 처리 및 줄바꿈을 <br> 태그로 변환
    return (formattedText || '').replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>');
  }

  if (!formattedText.formatting || formattedText.formatting.length === 0) {
    // HTML 엔티티 처리 및 줄바꿈을 <br> 태그로 변환
    return (formattedText.text || '').replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>');
  }

  const { text, formatting } = formattedText;
  let result = '';
  let lastIndex = 0;

  // 포맷팅을 시작 위치 기준으로 정렬
  const sortedFormatting = [...formatting].sort((a, b) => a.start - b.start);

  sortedFormatting.forEach((format) => {
    // 이전 포맷팅과 현재 포맷팅 사이의 텍스트 추가
    if (format.start > lastIndex) {
      const segment = text.substring(lastIndex, format.start);
      result += segment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>');
    }

    // 포맷팅 적용
    const formattedSegment = text.substring(format.start, format.end);
    let wrappedText = formattedSegment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>');

    switch (format.type) {
      case FORMAT_TYPES.BOLD:
        wrappedText = `<strong style="font-family: 'NanumSquareB', 'NanumSquare', sans-serif; font-weight: bold; font-style: normal; direction: ltr; text-align: left; writing-mode: horizontal-tb; unicode-bidi: normal;">${formattedSegment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>')}</strong>`;
        break;
      case FORMAT_TYPES.SIZE:
        const size = format.value || 1.2;
        wrappedText = `<span style="font-size: ${size}em; line-height: auto; font-style: normal; direction: ltr; text-align: left; writing-mode: horizontal-tb; unicode-bidi: normal;">${formattedSegment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>')}</span>`;
        break;
      case FORMAT_TYPES.TITLE:
        wrappedText = `<strong style="font-family: 'NanumSquareB', 'NanumSquare', sans-serif; font-weight: bold; font-size: 1.2em; line-height: auto; font-style: normal; display: inline; direction: ltr; text-align: left; writing-mode: horizontal-tb; unicode-bidi: normal;">${formattedSegment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>')}</strong>`;
        break;
      default:
        wrappedText = formattedSegment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>');
    }

    result += wrappedText;
    lastIndex = format.end;
  });

  // 마지막 포맷팅 이후의 텍스트 추가
  if (lastIndex < text.length) {
    const segment = text.substring(lastIndex);
    result += segment.replace(/&nbsp;/g, ' ').replace(/\n/g, '<br>');
  }

  return result;
};

// 포맷팅을 제거하고 순수 텍스트만 반환하는 함수
export const getPlainText = (formattedText) => {
  if (!formattedText) return '';
  if (typeof formattedText === 'string') return formattedText;
  return formattedText.text || '';
};

// 텍스트 선택 범위를 포맷팅 범위와 비교하여 중복 제거하는 함수
export const mergeFormatting = (existingFormatting, newFormatting) => {
  if (!existingFormatting || existingFormatting.length === 0) {
    return [newFormatting];
  }

  const result = [];
  let hasOverlap = false;

  existingFormatting.forEach((existing) => {
    // 새로운 포맷팅과 겹치는지 확인
    const hasStartOverlap = newFormatting.start < existing.end && newFormatting.end > existing.start;
    
    if (hasStartOverlap && existing.type === newFormatting.type) {
      // 같은 타입의 포맷팅이 겹치면 병합
      const merged = {
        start: Math.min(existing.start, newFormatting.start),
        end: Math.max(existing.end, newFormatting.end),
        type: existing.type,
        value: newFormatting.value || existing.value
      };
      result.push(merged);
      hasOverlap = true;
    } else {
      // 겹치지 않으면 기존 포맷팅 유지
      result.push(existing);
    }
  });

  if (!hasOverlap) {
    result.push(newFormatting);
  }

  return result;
};

// 포맷팅 제거 함수
export const removeFormatting = (existingFormatting, start, end, formatType) => {
  if (!existingFormatting || existingFormatting.length === 0) {
    return [];
  }

  return existingFormatting.filter((format) => {
    // 같은 타입이고 겹치는 범위가 있으면 제거
    if (format.type === formatType) {
      const hasOverlap = start < format.end && end > format.start;
      return !hasOverlap;
    }
    return true;
  });
};
